import * as PIXI from 'pixi.js';
import { ColorMapFilter } from 'pixi-filters';
import { Config } from '../../video-data/config';
import type { BaseSettings } from '../TimeAwareBase';
import { getGlobalAssetNetworkManager } from '../AssetNetworkManager';
import { shouldLoadMedia } from '../../types';
import { comparer, observable, reaction, runInAction, untracked } from '../reactivity';
import { Filter } from './Filter';

const DEFAULT_COLOR_MAP = PIXI.Texture.EMPTY;
const DEFAULT_FILTER_INTENSITY = 0.0;

type ColorMapState = { texture: PIXI.Texture; intensity: number } | null;

export class ColorGradingFilter extends Filter {
    override _classType = Config.COLOR_GRADING_FILTER;
    override pixiFilter: ColorMapFilter;

    #textureUrl: string = '';
    @observable accessor #textureUrlPendingLoad: string | null = null;
    #textureLoadFailed: boolean = false;
    #shouldLoad: boolean = false;
    @observable.ref accessor #texture: PIXI.Texture = DEFAULT_COLOR_MAP;
    @observable accessor #intensity: number = DEFAULT_FILTER_INTENSITY;

    static _textureCache: Map<string, PIXI.Texture> = new Map();

    constructor(settings: BaseSettings) {
        super(settings);
        this.#shouldLoad = shouldLoadMedia(settings.fetchResources);
        this.pixiFilter = new ColorMapFilter(DEFAULT_COLOR_MAP, false, DEFAULT_FILTER_INTENSITY);

        this.addReaction(
            reaction(
                () => ColorGradingFilter.#colorMapState(this),
                (state) => {
                    if (!state) {
                        return;
                    }
                    this.pixiFilter.colorMap = state.texture;
                    this.pixiFilter.mix = state.intensity;
                },
                { equals: comparer.shallow, name: 'ColorGradingFilter.colorMap' },
            ),
        );
    }

    /**
     * Yields nothing while a load is outstanding, so the filter keeps the texture it already had rather than
     * flashing the default one mid-load.
     */
    static #colorMapState(filter: ColorGradingFilter): ColorMapState {
        if (filter.#textureUrlPendingLoad !== null) {
            return null;
        }
        const texture = filter.#texture;
        // PIXI owns texture validity, and a texture is only assigned once it has loaded, so this is a guard on
        // the value we already have rather than something to wake on.
        const usable =
            texture === DEFAULT_COLOR_MAP || untracked(() => texture.baseTexture?.valid === true);
        if (!usable) {
            return null;
        }
        return { texture, intensity: filter.#intensity };
    }

    #setTexture(texture: PIXI.Texture, textureUrl: string): void {
        if (
            textureUrl &&
            this.#textureUrlPendingLoad &&
            this.#textureUrlPendingLoad !== textureUrl
        ) {
            // Skip apply if the texture URL is different from the one that is pending load
            return;
        }
        this.#textureUrl = textureUrl;
        runInAction(() => {
            this.#textureUrlPendingLoad = null;
            this.#texture = texture;
        });
    }

    #resetTextureProperties(): void {
        this.#textureUrl = '';
        runInAction(() => {
            this.#textureUrlPendingLoad = null;
            this.#texture = DEFAULT_COLOR_MAP;
            this.#intensity = DEFAULT_FILTER_INTENSITY;
        });
    }

    #onTextureLoadSuccess(
        texture: PIXI.Texture,
        textureUrl: string,
        storeInCache: boolean = true,
    ): void {
        this.#textureLoadFailed = false;
        this.#setTexture(texture, textureUrl);
        if (storeInCache) {
            ColorGradingFilter._textureCache.set(textureUrl, texture);
        }
        this.emit('onLoaded');
    }

    #onTextureLoadFailed(): void {
        this.emit('onLoaded');
        this.#textureLoadFailed = true;
        this.#resetTextureProperties();
    }

    /**
     * Load the texture from the given URL and apply it to the filter.
     * If the URL is empty, use the default color map.
     * If the texture is already in the cache, use it directly.
     * @param textureUrl The URL of the texture to load.
     */
    async #loadTexture(textureUrl: string): Promise<void> {
        if (!textureUrl) {
            this.#resetTextureProperties();
            return;
        } else if (ColorGradingFilter._textureCache.has(textureUrl)) {
            // Use texture that is already in the cache
            const texture: PIXI.Texture = ColorGradingFilter._textureCache.get(textureUrl)!;
            if (texture.baseTexture?.valid) {
                this.#onTextureLoadSuccess(texture, textureUrl, false);
                return;
            }
        }
        return new Promise((resolve, reject) => {
            const networkManager = getGlobalAssetNetworkManager();
            networkManager.waitForNetworkSlot(this, (requestFinished) => {
                const texture = PIXI.Texture.from(textureUrl);
                // Check if valid texture already exists
                if (texture.baseTexture?.valid) {
                    this.#onTextureLoadSuccess(texture, textureUrl);
                    requestFinished();
                    resolve();
                    return;
                }

                texture.baseTexture.on('loaded', () => {
                    this.#onTextureLoadSuccess(texture, textureUrl);
                    requestFinished();
                    resolve();
                });
                texture.baseTexture.on('error', (error) => {
                    this.#onTextureLoadFailed();
                    requestFinished();
                    reject(error);
                });
            });
        });
    }

    set textureUrl(value: string) {
        if (this.#textureUrl === value) {
            return;
        }
        if (this.#textureUrlPendingLoad === value) {
            return;
        }
        if (!this.#shouldLoad) {
            return;
        }
        if (this.#textureUrlPendingLoad) {
            // If a texture is already loading, we will overwrite it with the new one
            console.warn('Ongoing texture load will be replaced by new texture');
        }
        this.#textureUrlPendingLoad = value;
        this.#loadTexture(value)
            .catch((error) => {
                console.error('Error loading texture:', error);
                throw error;
            })
            .finally(() => {
                this.#textureUrlPendingLoad = null;
                this.requiresUpdate = true;
            });
    }

    get textureUrl(): string {
        return this.#textureUrl;
    }

    set intensity(value: number) {
        this.#intensity = value;
        this.requiresUpdate = true;
    }

    get intensity(): number {
        return this.#intensity;
    }

    get isLoaded(): boolean {
        return this.#textureUrlPendingLoad === null;
    }

    get failedToLoad(): boolean {
        return this.#textureLoadFailed;
    }

    override destroy(): void {
        // Detatch instead of destroying the texture so that reference can be reused
        this.pixiFilter.colorMap = null;
        this.disposeReactions();
    }

    override isNeutral(): boolean {
        return this.#textureUrl === '' && this.#intensity === DEFAULT_FILTER_INTENSITY;
    }
}
